import { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon, useCheckBreakpoint } from '@marriott/mi-ui-library';

import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';

import { WebPromptBannerContent } from './WebPromptBannerContent/WebPromptBannerContent';
import { StyledWebPromptBanner } from './WebPromptBanner.styles';
import { mockPromptList } from './__mock__/WebPromptBanner.mock';
import { WebPromptBannerProps } from './WebPromptBanner.types';

export const WebPromptStaticBanner: FC<WebPromptBannerProps> = ({
  promptList = mockPromptList,
  isExternalLink = false,
  styleClass,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { webPromptSeenInSession, setWebPromptSeenInSession } = useAccountPersistentStore(state => state);
  const isTabletAndAboveViewPort = useCheckBreakpoint('viewportM');
  const sourceList = Array.isArray(promptList) ? promptList : [];
  const closeButtonLabel =
    (typeof window !== 'undefined'
      ? (window as Window & { commonPageContent?: { closeButton?: string } }).commonPageContent?.closeButton
      : undefined) || 'Close';

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const visiblePrompts = sourceList.filter(prompt => {
    const key = String(prompt?.promptKey || '')
      .trim()
      .toUpperCase();
    return Boolean(key) && !webPromptSeenInSession?.[key];
  });

  if (!isHydrated || !visiblePrompts.length) return null;

  const handleClose = (promptKey: string) => {
    setWebPromptSeenInSession({
      promptKey,
      isSeen: true,
    });
  };

  return (
    <StyledWebPromptBanner className={clsx('container mb-3 web-prompt-static-banner', styleClass)} data-testid="account-WebPromptStaticBanner">
      {visiblePrompts.map((prompt, index) => {
        if (!prompt || !prompt.promptKey?.trim()) return null;

        const promptKey = String(prompt?.promptKey || '')
          .trim()
          .toUpperCase();
        const updatedCtaPath = isTabletAndAboveViewPort
          ? prompt.promptRedirectURL
          : prompt.mobilePromptRedirectURL || prompt.promptRedirectURL;

        return (
          <div className={clsx('col-sm-12 px-0', index < visiblePrompts.length - 1 && 'mb-2')} key={promptKey}>
            <div className="card d-flex align-items-start py-3 flex-sm-row flex-lg-row mb-0 web-prompt-banner">
              <div className="d-flex outer ml-3 ml-md-4">
                <div className="icon d-flex">
                  <Icon iconClass={`${prompt.promptIcon} m-auto`} />
                </div>
              </div>
              <div className="d-flex banner-content">
                <WebPromptBannerContent
                  title={prompt.promptTitle}
                  subtitle={prompt.promptSubTitle}
                  subtitleClassName="t-font-s"
                  ctaLabel={prompt.promptCTALabel}
                  ctaPath={updatedCtaPath}
                  isExternalLink={isExternalLink}
                />
              </div>
              <span
                className="icon-clear mr-3"
                tabIndex={0}
                role="button"
                onClick={() => handleClose(promptKey)}
                aria-label={closeButtonLabel}
                data-testid="close-icon"
              ></span>
            </div>
          </div>
        );
      })}
    </StyledWebPromptBanner>
  );
};
